import { useState, useEffect } from 'react';
import { getAmplifyClient } from '../../../lib/amplifyClient';

export interface ImportMonthSummary {
  yearMonth: string;
  importDate: string;
  hoursCount: number;
  totalConsumption: number;
  isExtrapolated: boolean;
}

export interface ImportSummaryResult {
  success: boolean;
  year: number;
  totalMonths: number;
  imports: ImportMonthSummary[];
  message?: string;
}

export interface UseGetImportSummaryReturn {
  data: ImportSummaryResult | null;
  loading: boolean;
  error: string | null;
  refetch: (year: number) => Promise<void>;
}

export function useGetImportSummary(year: number): UseGetImportSummaryReturn {
  const [data, setData] = useState<ImportSummaryResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSummary = async (yearToFetch: number) => {
    setLoading(true);
    setError(null);

    try {
      const client = getAmplifyClient();
      const inputJson = JSON.stringify({ year: yearToFetch });

      const result = await client.queries.getImportSummary({
        input: inputJson,
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      if (!result.data) {
        throw new Error('No data returned from getImportSummary');
      }

      // Parse the AWSJSON response with type guard
      // AppSync may return already-parsed object or stringified JSON
      let parsedResult: any;

      if (typeof result.data === 'string') {
        // Case 1: String response - parse it
        try {
          parsedResult = JSON.parse(result.data);

          // Case 1a: Double-stringified - parse again
          if (typeof parsedResult === 'string') {
            parsedResult = JSON.parse(parsedResult);
          }
        } catch (parseError) {
          throw new Error(
            `Failed to parse response: ${parseError instanceof Error ? parseError.message : 'Unknown parse error'}`
          );
        }
      } else if (typeof result.data === 'object' && result.data !== null) {
        // Case 2: Already parsed object - use directly
        parsedResult = result.data;
      } else {
        // Case 3: Unexpected type
        throw new Error(
          `Unexpected response type: ${typeof result.data}. Expected string or object.`
        );
      }

      const typedResult: ImportSummaryResult = parsedResult;

      setData(typedResult);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(message);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (year) {
      void fetchSummary(year);
    }
  }, [year]);

  return {
    data,
    loading,
    error,
    refetch: fetchSummary,
  };
}
